import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "../../TrialCheckout.css";
import {
  Box,
  Grid,
  Typography,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Skeleton,
  Paper,
  TextField,
  // InputAdornment,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
// import { LocalOffer } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { backend_api, stripe_token } from "../../static/server";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";

const stripePromise = loadStripe(stripe_token);

const parseStoredUser = (value) => {
  try {
    const parsedValue = JSON.parse(value || "null");
    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
  } catch {
    return {};
  }
};

const CheckoutForm = ({
  plan,
  onSuccess,
  clientSecret,
  sessionData,
  appliedCoupon,
  couponDiscount,
  onApplyCoupon,
  onRemoveCoupon,
  applyingCoupon,
  couponError,
  couponCode,
  setCouponCode,
  showCouponInput,
  setShowCouponInput,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const { token, user } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  const loginUserData = useMemo(() => {
    const storedUser = parseStoredUser(localStorage.getItem("userData"));
    return Object.keys(storedUser).length ? storedUser : user || {};
  }, [user]);

  useEffect(() => {
    const fullName = [loginUserData?.first_name, loginUserData?.last_name]
      .filter(Boolean)
      .join(" ");

    setFormData((prev) => ({
      ...prev,
      name: fullName || "",
      email: loginUserData?.reminder_mail || "",
    }));
  }, [loginUserData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    if (!formData.name || !formData.email) {
      toast.error("Please fill in all fields");
      setProcessing(false);
      return;
    }

    if (!stripe || !elements) {
      setProcessing(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    const paymentMethod = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardNumberElement,
        billing_details: {
          name: formData.name,
          email: formData.email,
        },
      },
    });

    // console.log("PaymentMethod :", paymentMethod);
    // console.log("PaymentMethod ID:", paymentMethod?.paymentIntent?.id);

    if (paymentMethod?.paymentIntent?.id) {
      try {
        const payload = {
          payment_intent_id: paymentMethod?.paymentIntent?.id,
          name: formData.name,
          email: formData.email,
          subscription_plan: plan.id,
        };
        if (appliedCoupon) {
          payload.coupon_code = appliedCoupon;
        }

        const res = await axios.post(`${backend_api}one-time-order/`, payload);

        // console.log("Backend response:", res.data);

        if (res.data) {
          toast.success("Payment successful!");
          onSuccess(paymentMethod.paymentIntent.id);
        } else {
          toast.error(res.data.message || "Payment failed");
        }
      } catch (err) {
        console.error("Backend error:", err);
        toast.error(err.response?.data?.message || "Payment processing failed");
      }
    }

    setProcessing(false);
  };

  const formatPrice = (price) => {
    if (!price) return "";
    const num = parseFloat(price);
    return `$${num.toFixed(2)}`;
  };

  const originalPrice = plan.one_time_payment
    ? parseFloat(plan.one_time_payment)
    : plan.price_dollar
      ? parseFloat(plan.price_dollar)
      : null;

  const discountedTotal =
    appliedCoupon && couponDiscount && originalPrice
      ? originalPrice - couponDiscount
      : null;

  return (
    <form onSubmit={handleSubmit}>
      <Paper elevation={0} sx={{ p: 3, bgcolor: "#f9f9f9", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Billing Information
        </Typography>

        {/* Name Field */}
        <TextField
          fullWidth
          label="Full Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          margin="normal"
          size="small"
        />

        {/* Email Field */}
        <TextField
          fullWidth
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          margin="normal"
          size="small"
        />

        {/* Coupon Section */}
        {/* <Box sx={{ my: 2 }}>
          {appliedCoupon ? (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={onRemoveCoupon}>
                  Remove
                </Button>
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalOffer fontSize="small" />
                <Typography variant="body2">
                  Coupon <strong>{appliedCoupon}</strong> applied! You save $
                  {couponDiscount?.toFixed(2)}
                </Typography>
              </Box>
            </Alert>
          ) : (
            <Box sx={{ width: "100%" }}>
              {showCouponInput ? (
                <TextField
                  size="small"
                  fullWidth
                  label="Coupon Code"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                  }}
                  disabled={applyingCoupon || processing}
                  error={!!couponError}
                  helperText={couponError}
                  InputProps={{
                    endAdornment: couponCode.trim() && (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={onApplyCoupon}
                          disabled={applyingCoupon}
                        >
                          {applyingCoupon ? "Applying..." : "Apply"}
                        </Button>
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOffer sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Button
                  startIcon={<LocalOffer />}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => setShowCouponInput(true)}
                  sx={{
                    borderStyle: "dashed",
                    borderColor: "primary.main",
                    color: "primary.main",
                  }}
                >
                  Have a coupon code?
                </Button>
              )}
            </Box>
          )}
        </Box> */}

        {/* Price summary */}
        {appliedCoupon && discountedTotal !== null && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1.5,
              bgcolor: "#e8f5e9",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">Total after discount:</Typography>
            <Typography variant="h6" sx={{ color: "success.main" }}>
              {formatPrice(discountedTotal)}
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Card Details
        </Typography>

        {/* Card Number */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Card Number
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mt: 0.5,
              "&:hover": { borderColor: "#000" },
            }}
          >
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                  },
                },
              }}
            />
          </Paper>
        </Box>

        <Grid container spacing={2}>
          {/* Expiry Date */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Expiry Date
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mt: 0.5,
                "&:hover": { borderColor: "#000" },
              }}
            >
              <CardExpiryElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": { color: "#aab7c4" },
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* CVC */}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              CVC
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mt: 0.5,
                "&:hover": { borderColor: "#000" },
              }}
            >
              <CardCvcElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": { color: "#aab7c4" },
                    },
                  },
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          disabled={!stripe || processing}
        >
          {processing ? "Processing..." : "Pay Now"}
        </Button>
      </Paper>
    </form>
  );
};

// --------------------- Main TrialCheckout Component ---------------------
const TrialCheckout = () => {
  const navigate = useNavigate();
  const [planLoading, setPlanLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Refs to prevent double API calls in StrictMode
  const hasFetchedPlan = useRef(false);
  const hasCreatedSession = useRef(false);

  // Fetch plan details
  useEffect(() => {
    if (hasFetchedPlan.current) return;
    hasFetchedPlan.current = true;

    const fetchPlan = async () => {
      try {
        const res = await axios.get(
          `${backend_api}plans/?billing_cycle=yearly`,
        );
        const plans = res.data.results || [];

        const targetPlan = plans.find(
          (p) =>
            p.name?.toLowerCase() === "advance" ||
            p.one_time_payment > "1.00" ||
            p.total_expiry_time === 7300,
        );

        if (targetPlan) {
          setPlan(targetPlan);
        } else {
          const fallbackPlan = plans.find((p) => p.one_time_payment);
          if (fallbackPlan) {
            setPlan(fallbackPlan);
          } else {
            toast.error("Special one-time plan not found");
          }
        }
      } catch (error) {
        toast.error("Failed to load plan details");
        console.error(error);
      } finally {
        setPlanLoading(false);
      }
    };

    fetchPlan();
  }, []);

  // Create payment session (with or without coupon)
  const createPaymentSession = useCallback(
    async (coupon = null) => {
      if (!plan?.id) return;

      setSessionLoading(true);
      try {
        const payload = {
          subscription_plan: plan.id,
        };
        if (coupon) {
          payload.coupon_code = coupon;
        }

        const res = await axios.post(
          `${backend_api}one-time-payment-session/`,
          payload,
        );
        setSessionData(res.data);
        setClientSecret(res.data.client_secret);
      } catch (error) {
        toast.error("Failed to initialize payment");
        console.error(error);
      } finally {
        setSessionLoading(false);
      }
    },
    [plan?.id],
  );

  // Create initial session when plan loads
  useEffect(() => {
    if (!plan?.id) return;
    if (hasCreatedSession.current) return;
    hasCreatedSession.current = true;

    createPaymentSession();
  }, [plan?.id, createPaymentSession]);

  // Validate and apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setApplyingCoupon(true);
    setCouponError("");

    try {
      const res = await axios.get(
        `${backend_api}validate-coupon/?code=${couponCode}`,
      );

      if (res?.data?.valid && res?.data?.flat_discount) {
        const discount = parseFloat(res.data.flat_discount);

        // Optional: check if discount is not greater than price
        const originalPrice = parseFloat(plan.one_time_payment);
        if (discount >= originalPrice) {
          setCouponError("Discount cannot exceed the plan price");
          return;
        }

        setCouponDiscount(discount);
        setAppliedCoupon(couponCode.trim());

        // Re‑create payment session with the coupon
        await createPaymentSession(couponCode.trim());

        toast.success(`Coupon applied! You save $${discount.toFixed(2)}`);
        setShowCouponInput(false);
      } else {
        setCouponError("Invalid coupon code");
        toast.error("Invalid coupon code");
      }
    } catch (error) {
      setCouponError("Failed to validate coupon");
      toast.error("Failed to validate coupon");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setAppliedCoupon(null);
    setCouponDiscount(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponInput(false);

    await createPaymentSession();
    toast.info("Coupon removed");
  };

  const handleSuccess = (paymentIntentId) => {
    navigate(`/checkout-50/status?payment_intent_id=${paymentIntentId}`);
  };

  // console.log(plan);
  // console.log(clientSecret);

  if (planLoading || sessionLoading) {
    return (
      <Box sx={{ py: "2.5rem" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={10} lg={9}>
            <Card sx={{ p: 4 }}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" width="80%" height={60} />
              <Skeleton variant="rectangular" height={200} sx={{ my: 2 }} />
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box sx={{ py: "2.5rem", textAlign: "center" }}>
        <Typography color="error">
          Special one-time plan is not available at the moment.
        </Typography>
      </Box>
    );
  }

  const priceDisplay = plan.one_time_payment
    ? `$${parseFloat(plan.one_time_payment).toFixed(2)}`
    : plan.price_dollar
      ? `$${plan.price_dollar}`
      : "Custom";

  return (
    <Box sx={{ py: "2.5rem" }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={9}>
          <Card sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* LEFT SIDE - ORDER SUMMARY */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LockIcon color="success" />
                  <Typography variant="subtitle2" color="success.main">
                    LIMITED FOUNDING ACCESS
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {plan.name} – Lifetime Access
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={2}>
                  {plan.description ||
                    "Growth Operating System for law firms under $1M — structured Matter management, workflow control, and AI-guided recommendations."}
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" fontWeight="bold">
                  Order Summary
                </Typography>
                <Typography variant="body2" mt={1}>
                  <strong>Item:</strong> {plan.name} Lifetime Access
                </Typography>
                <Typography variant="body2">
                  <strong>Users Included:</strong> Up to {plan.user_limit || 3}{" "}
                  users
                </Typography>
                <Typography variant="body2">
                  <strong>Price:</strong> {priceDisplay} (One-Time Payment)
                </Typography>

                {appliedCoupon && couponDiscount && (
                  <Typography variant="body2" sx={{ color: "success.main" }}>
                    <strong>Discount:</strong> -$
                    {couponDiscount.toFixed(2)} (coupon: {appliedCoupon})
                  </Typography>
                )}

                <Typography variant="h6" mt={2} color="primary">
                  Order Total:{" "}
                  {appliedCoupon && couponDiscount
                    ? `$${(
                        parseFloat(plan.one_time_payment) - couponDiscount
                      ).toFixed(2)}`
                    : priceDisplay}
                </Typography>
                <Typography variant="caption" display="block" mb={3}>
                  No recurring charges. No subscription.
                </Typography>

                {clientSecret && sessionData && (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm
                      plan={plan}
                      onSuccess={handleSuccess}
                      clientSecret={clientSecret}
                      sessionData={sessionData}
                      // Coupon props
                      appliedCoupon={appliedCoupon}
                      couponDiscount={couponDiscount}
                      onApplyCoupon={handleApplyCoupon}
                      onRemoveCoupon={handleRemoveCoupon}
                      applyingCoupon={applyingCoupon}
                      couponError={couponError}
                      couponCode={couponCode}
                      setCouponCode={setCouponCode}
                      showCouponInput={showCouponInput}
                      setShowCouponInput={setShowCouponInput}
                    />
                  </Elements>
                )}
              </Grid>

              {/* RIGHT SIDE - WHAT'S INCLUDED */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight="bold">
                  What’s Included in {plan.name}
                </Typography>

                <List dense>
                  {plan.features && plan.features.length > 0
                    ? plan.features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.featureName || feature}
                          />
                        </ListItem>
                      ))
                    : [
                        "Full Matter Management Platform",
                        "Structured Workflow & Matter Tracking",
                        "7-Stage Growth Framework",
                        "All Future AI Managers Included",
                        "Lifetime Access (Never Offered Again)",
                        "Private Founders Circle",
                        "Priority Onboarding Support",
                        "AI Implementation Playbook (PDF)",
                      ].map((item, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                </List>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" fontWeight="bold">
                  30-Day Founding Partner Setup Guarantee
                </Typography>

                <Alert severity="success" sx={{ mt: 2 }}>
                  If we can’t get your firm fully set up within 30 days, we’ll
                  personally troubleshoot — or refund 100%.
                </Alert>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" fontWeight="bold">
                  Important Before You Complete
                </Typography>

                <List dense>
                  {[
                    `Includes up to ${plan.user_limit || 3} users (20 Years)`,
                    "AI modules roll out progressively",
                    "Implementation required",
                    "Lifetime access never offered again",
                    "Built for firms serious about structure",
                  ].map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="body2" align="center" color="text.secondary">
              If your transaction doesn’t go through, it’s often a card
              verification issue. Call the number on the back of your card. Need
              help? Email support@lexmom.com
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrialCheckout;
